import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';

import { IBaseProps } from '@rbi-ctg/frontend';
import { IEncryptionResult } from 'components/payments/integrations/orbital/components/encryption/types';
import {
  IGetEncryptionDetailsMutation,
  IMergePrepaidInput,
  IMergePrepaidPayload,
  UserAccountsDocument,
  useAddCreditAccountMutation,
  useGetEncryptionDetailsMutation,
} from 'generated/graphql-gateway';
import useEffectOnce from 'hooks/use-effect-once';
import useErrorModal from 'hooks/use-error-modal';
import { useAuthContext } from 'state/auth';
import { useLocale } from 'state/intl';
import { PaymentFieldVariations } from 'state/launchdarkly/variations';
import { useMParticleContext } from 'state/mParticle';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { IPaymentPayload, IPaymentState, initialPaymentState } from 'utils/payment';
import { useMemoAll } from 'utils/use-memo-all';

import usePayment from './hooks/use-payment';
import {
  IAddPaymentMethodOptions,
  IPaymentDetails,
  IPaymentMethod,
  IReloadPrepaidCard,
} from './types';

export interface IPaymentContext {
  addPaymentMethod: (
    method: IPaymentPayload,
    options?: IAddPaymentMethodOptions,
    encryptionResult?: IEncryptionResult
  ) => Promise<string>;
  checkoutPaymentMethod: IPaymentMethod | undefined;
  checkoutPaymentMethodId: string;
  canUseApplePay: boolean;
  canUseGooglePay: boolean;
  clearPaymentDetails(): void;
  defaultPaymentMethodId: string;
  defaultReloadPaymentMethodId: string;
  deletePaymentMethod: (accountId: string) => Promise<void>;
  encryptionResult: IEncryptionResult | undefined;
  getBalanceFromPaymentMethods: (prepaidPaymentMethod: IPaymentMethod) => number;
  getCounter?: () => Promise<void>;
  getEncryptionDetails: () => Promise<
    Pick<
      IGetEncryptionDetailsMutation['encryptionDetails'],
      'fdPublicKey' | 'fdApiKey' | 'fdAccessToken' | 'fdCustomerId' | 'algorithm'
    >
  >;
  getPaymentMethods: () => Promise<void>;
  getPrepaidPaymentMethod: () => IPaymentMethod | null;
  getPrepaidCardNumber: () => string | null;
  hasGetPaymentMethodsError: boolean;
  isFirstData: boolean;
  isOrbital: boolean;
  loading: boolean;
  mergePrepaidCardBalances: (input: IMergePrepaidInput) => Promise<IMergePrepaidPayload>;
  paymentMethods: IPaymentMethod[];
  paymentDetails: IPaymentDetails;
  paymentProcessor: string | null | undefined;
  prepaidReloadPaymentMethodId: string;
  reloadPaymentValues: IPaymentState;
  reloadPrepaidCard: (input: IReloadPrepaidCard) => Promise<number | undefined>;
  setCheckoutPaymentMethodId: (accountId: string) => void;
  setEncryptionResult: (encryptionResult: IEncryptionResult | undefined) => void;
  setDefaultPaymentMethodId: (accountId: string) => void;
  setDefaultReloadPaymentMethodId: (accountId: string) => void;
  setPrepaidReloadPaymentMethodId: (accountId: string) => void;
  setSelected?: (fdAccountId: string) => void;
  setReloadPaymentValues: Dispatch<SetStateAction<IPaymentState>>;
  storePaymentDetails: (data: IPaymentDetails) => void;
  transformPaymentValues: ({
    paymentValues,
    paymentFieldVariations,
  }: {
    paymentValues: IPaymentState;
    paymentFieldVariations: PaymentFieldVariations;
  }) => IPaymentPayload;
  setLoading: (input: boolean) => void;
  isQuickPayGiftCardMethodSelected: boolean;
  setIsQuickPayGiftCardMethodSelected: (input: boolean) => void;
}

export const PaymentContext = createContext<IPaymentContext>({} as IPaymentContext);
export const usePaymentContext = () => useContext(PaymentContext);

const clearPersistedPaymentDetails = (): void => {
  LocalStorage.setItem(StorageKeys.PAYMENT, {});
};

const persistPaymentDetails = (paymentDetails: IPaymentDetails): void => {
  LocalStorage.setItem(StorageKeys.PAYMENT, paymentDetails);
};

const retrievePersistedPaymentDetails = (): IPaymentDetails | null => {
  return LocalStorage.getItem(StorageKeys.PAYMENT);
};

export function PaymentProvider(props: IBaseProps) {
  const { feCountryCode } = useLocale();
  const { updateUserInfo, user } = useAuthContext();
  const [getEncryptionDetailsMutation] = useGetEncryptionDetailsMutation();
  const [addCreditAccountMutation] = useAddCreditAccountMutation({
    awaitRefetchQueries: true,
    refetchQueries: [{ query: UserAccountsDocument, variables: { feCountryCode } }],
  });
  const { logEvent, updateUserAttributes } = useMParticleContext();

  const [paymentDetails, setPaymentDetails] = useState({});
  const [isQuickPayGiftCardMethodSelected, setIsQuickPayGiftCardMethodSelected] = useState(false);

  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Payment Error',
  });

  const [reloadPaymentValues, setReloadPaymentValues] = useState(
    initialPaymentState({ billingCountry: feCountryCode, userDetailsName: user?.details?.name })
  );

  const storePaymentDetails = useCallback((data: IPaymentDetails): void => {
    setPaymentDetails(data);
    persistPaymentDetails(data);
  }, []);

  const clearPaymentDetails = useCallback(() => {
    setPaymentDetails({});
    clearPersistedPaymentDetails();
  }, []);

  useEffectOnce(() => {
    const values = retrievePersistedPaymentDetails();
    if (values) {
      setPaymentDetails(values);
    }
  });

  const {
    addPaymentMethod,
    canUseApplePay,
    canUseGooglePay,
    checkoutPaymentMethod,
    checkoutPaymentMethodId,
    defaultPaymentMethodId,
    defaultReloadPaymentMethodId,
    deletePaymentMethod,
    encryptionResult,
    setEncryptionResult,
    getEncryptionDetails,
    getBalanceFromPaymentMethods,
    getPaymentMethods,
    getPrepaidCardNumber,
    getPrepaidPaymentMethod,
    hasGetPaymentMethodsError,
    isFirstData,
    isOrbital,
    loading,
    mergePrepaidCardBalances,
    paymentMethods,
    paymentProcessor,
    prepaidReloadPaymentMethodId,
    reloadPrepaidCard,
    setCheckoutPaymentMethodId,
    setDefaultPaymentMethodId,
    setDefaultReloadPaymentMethodId,
    setPrepaidReloadPaymentMethodId,
    transformPaymentValues,
    setLoading,
  } = usePayment({
    getEncryptionDetailsMutation,
    logEvent,
    updateUserAttributes,
    openErrorDialog,
    user,
    updateUserInfo,
    addCreditAccountMutation,
  });

  const value = useMemoAll({
    addPaymentMethod,
    canUseApplePay,
    canUseGooglePay,
    checkoutPaymentMethod,
    checkoutPaymentMethodId,
    clearPaymentDetails,
    encryptionResult,
    setEncryptionResult,
    defaultPaymentMethodId,
    defaultReloadPaymentMethodId,
    deletePaymentMethod,
    getPaymentMethods,
    getBalanceFromPaymentMethods,
    getPrepaidPaymentMethod,
    getPrepaidCardNumber,
    getEncryptionDetails,
    hasGetPaymentMethodsError,
    isFirstData,
    isOrbital,
    loading,
    mergePrepaidCardBalances,
    paymentMethods,
    paymentProcessor,
    paymentDetails,
    prepaidReloadPaymentMethodId,
    reloadPaymentValues,
    reloadPrepaidCard,
    setCheckoutPaymentMethodId,
    setDefaultPaymentMethodId,
    setDefaultReloadPaymentMethodId,
    setPrepaidReloadPaymentMethodId,
    storePaymentDetails,
    setReloadPaymentValues,
    transformPaymentValues,
    setLoading,
    isQuickPayGiftCardMethodSelected,
    setIsQuickPayGiftCardMethodSelected,
  });

  return (
    <PaymentContext.Provider value={value}>
      {props.children}
      <ErrorDialog />
    </PaymentContext.Provider>
  );
}

export default PaymentContext.Consumer;
